
  import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from '@azure/msal-react';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  McTopBar,
  McButton,
  McTable,
  McSelect,
  McOption,
  McInputDate,
  McInput,
  McIcon,
  McPagination
} from "@maersk-global/mds-react-wrapper/components-core";
import { McMultiSelect } from "@maersk-global/mds-react-wrapper/components-core/mc-multi-select";
import "./Home.css";
import FilterSelector from "./FilterSelector";
import { AL } from "../../al";
import { jwtDecode } from "jwt-decode";



const FILTER_CONFIG = [
  { key: "chargeId",label: "Charge Notice ID",placeholder: "Enter one or more IDs, separated by commas",type: "text" },
  { key: "foNumber",label: "FO Number",placeholder: "Enter one or more FO numbers, separated by commas",type: "text"},
  { key: "billOfLading",label: "Bill of Lading",placeholder: "Enter one or more BOL values, separated by commas",type: "text" },
  { key: "containerReference",label: "Container Reference",placeholder: "Enter one or more containers, separated by commas",type: "text" },
  { key: "approver",label: "Approver",placeholder: "Enter one or more approvers, separated by commas",type: "text"},
  { key: "requestor",label: "Owner",placeholder: "Enter one or more owners, separated by commas",type: "text" },
  { key: "plant",label: "Plant Code",placeholder: "Enter one or more plants, separated by commas",type: "text" },
  { key: "poCreator", label: "PO Creator", placeholder: "Enter one or more PO creators, separated by commas", type: "text" },
  { key: "status", label: "Status", placeholder: "Select Status (multiple allowed)", type: "multiselect" },
  { key: "segment", label: "Segment", placeholder: "Select Segment", type: "select" },
  { key: "mrsNonMrs", label: "MRS/Non-MRS", placeholder: "Select MRS/Non-MRS (multiple allowed)", type: "multiselect" },
  { key: "dateRange", label: "Date Range", type: "dateRange" },
  { key: "destinationCountry", label: "Destination Country", placeholder: "Enter Destination Country", type: "text" }
];

const Home = () => {
  const [notices, setNotices] = useState([]);
  const [filteredNotices, setFilteredNotices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const { instance } = useMsal();
  const [userInfo, setUserInfo] = useState(null);
  const [roles, setRoles] = useState([]);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [visibleFilters, setVisibleFilters] = useState([]);
  const [filters, setFilters] = useState({
    approver: "",
    requestor: "",
    poCreator: "",
    segment: "",
    mrsNonMrs: [],
    destinationCountry: "",
    plant: "",
    createdDateFrom: null,
    createdDateTo: null,
    chargeId: "",
    foNumber: "",
    region: "",
    billOfLading: "",
    containerReference: "",
    status: []
  });
  const [appliedFilters, setAppliedFilters] = useState({
    approver: "",
    requestor: "",
    poCreator: "",
    segment: "",
    mrsNonMrs: [],
    destinationCountry: "",
    plant: "",
    createdDateFrom: null,
    createdDateTo: null,
    chargeId: "",
    foNumber: "",
    region: "",
    billOfLading: "",
    containerReference: "",
    status: []
  });
  const filtersRef = useRef(filters);
  const currentFiltersRef = useRef(filters); // Track current filters for applyFilters
  const [filterOptions, setFilterOptions] = useState({
    approvers: [],
    requestors: [],
    destinationCountries: [],
    plants: [],
    status: [],
    segment: [],
    mrs_non_mrs: []
  });

  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
    total_records: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false
  });

  // ── Master Search state ──────────────────────────────────────────────────
  const [masterSearchInput, setMasterSearchInput] = useState("");
  const [masterSearchQuery, setMasterSearchQuery] = useState("");
  const [isMasterSearchMode, setIsMasterSearchMode] = useState(false);
  const isMasterSearchModeRef = useRef(false);
  const masterSearchQueryRef = useRef("");
  const [masterPagination, setMasterPagination] = useState({
    page: 1,
    page_size: 50,
    total_records: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false
  });

  // ── Include Comments toggle (applies to both download paths) ─────────────
  const [includeComments, setIncludeComments] = useState(false);
  const includeCommentsRef = useRef(false);

  const CACHE_KEY = "FO_CHARGES_CACHE";
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  const isHydratedRef = useRef(false);

  const getUserInitials = (email) => {
    if (!email) return "??";
    const local = email.split("@")[0];
    const parts = local.split(".");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return local.substring(0, 2).toUpperCase();
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);



  const navigate = useNavigate();
  const account = userInfo;
  const rolesDetails = roles;
  let hasAccess = false;
  if (rolesDetails) {
    hasAccess = rolesDetails.includes('IDAMaerskCX') || rolesDetails.includes('MaerskInternalUser');

  }
  
  let hasAccessbulk = false;
  if (rolesDetails) {
    hasAccessbulk = rolesDetails.includes('IDAMaerskCX') || rolesDetails.includes('IDAMaerskBilling') || rolesDetails.includes('IDAVestasApprover');
  }

  let hasAnalyticsAccess = false;
  if (rolesDetails) {
    hasAnalyticsAccess = rolesDetails.includes('IDAMaerskCX') || rolesDetails.includes('IDAVestasViewer') || rolesDetails.includes('IDAMaerskViewer') || rolesDetails.includes('IDAMaerskBilling')|| rolesDetails.includes('IDAVestasApprover');
  }

  // Bulk Reassign — admin-only, driven by REACT_APP_PO_ADMIN_EMAILS env var
  const PO_ADMIN_EMAILS = (process.env.REACT_APP_PO_ADMIN_EMAILS || "")
    .split(",")
    .map(e => e.trim().toLowerCase())
    .filter(Boolean);
  const hasReassignAccess = account ? PO_ADMIN_EMAILS.includes(account.toLowerCase()) : false;

  // Approver Matrix — now open to all logged-in users.
  // Edit/Delete rights inside the page are still restricted to REACT_APP_PO_ADMIN_EMAILS.
  const hasApproverMatrixAccess = true;

  console.log("🔐 [Bulk Reassign Access Check]", {
    loggedInEmail: account || "not loaded yet",
    allowedAdmins: PO_ADMIN_EMAILS,
    hasReassignAccess
  });
  useEffect(() => {
    // Example: get token from localStorage or API
    const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
    if (token) {
      try {
        const decoded = jwtDecode(token); // decode JWT
        setUserInfo(decoded.email);
        setRoles(decoded.roles || []);

      } catch (err) {
        console.error("Invalid token", err);
      }
    }
  }, []);

  useEffect(() => {
    const cached = loadCache();
    if (cached) {
      currentFiltersRef.current = cached.filters; // Sync ref immediately
      setFilters(cached.filters);
      setAppliedFilters(cached.filters);
      setPagination(cached.pagination);
      setNotices(cached.notices);
      setFilteredNotices(cached.notices);
      
      // Derive visible filters from cached filter values
      const activeFilterKeys = Object.entries(cached.filters)
        .filter(([key, value]) => {
          if (key === 'createdDateFrom' || key === 'createdDateTo') {
            return value !== null;
          }
          return Array.isArray(value) ? value.length > 0 : (value && value !== "");
          if (key === 'createdDateFrom' || key === 'createdDateTo') {
            return 'dateRange';
          }
          return key;
        })
        .filter((key, index, self) => self.indexOf(key) === index); // Remove duplicates
      
      // Only include keys that exist in FILTER_CONFIG
      const validFilterKeys = activeFilterKeys.filter(key => 
        FILTER_CONFIG.some(f => f.key === key)
      );
      
      setVisibleFilters(validFilterKeys);
      isHydratedRef.current = true;   // 👈 important
    }
  }, []);

  // Fetch filter values on component mount
  useEffect(() => {
    const fetchFilterValues = async () => {
      try {
        const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/filter-values`, {
          headers: {
            "Authorization": `Bearer ${token}`,
            "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
          }
        });
        const json = await res.json();
        setFilterOptions(json);
      } catch (err) {
        console.error("Error fetching filter values:", err);
      }
    };
    fetchFilterValues();
  }, []);

  // ── Master Search fetch ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isMasterSearchMode || !masterSearchQuery) return;

    const fetchMasterSearch = async () => {
      setTableLoading(true);
      try {
        const queryParams = new URLSearchParams({
          query: masterSearchQuery,
          pageNumber: masterPagination.page,
          pageSize: masterPagination.page_size,
        });
        const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/master-search?${queryParams}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY,
            },
          }
        );
        const json = await res.json();
        setNotices(json.data || []);
        setFilteredNotices(json.data || []);
        // Coerce page/page_size to numbers for the same reason as the main
        // fetchNotices effect: a string echoed back from the API would fail
        // Object.is against this effect's own numeric dependency values.
        setMasterPagination(prev => {
          const p = { ...prev, ...(json.pagination || {}) };
          return { ...p, page: Number(p.page) || prev.page, page_size: Number(p.page_size) || prev.page_size };
        });
      } catch (err) {
        console.error("Master search error:", err);
      } finally {
        setTableLoading(false);
      }
    };

    fetchMasterSearch();
  }, [isMasterSearchMode, masterSearchQuery, masterPagination.page, masterPagination.page_size]);

  useEffect(() => {
  // 🚫 Skip FIRST fetch if hydrated from cache
  if (isHydratedRef.current) {
    isHydratedRef.current = false; // consume flag ONCE
    return;
  }
  // 🚫 Skip when master search is active
  if (isMasterSearchMode) return;

  const fetchNotices = async () => {
    setTableLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pagination.page,
        page_size: pagination.page_size,
        ...(appliedFilters.approver && { approver: appliedFilters.approver }),
        ...(appliedFilters.requestor && { requestor: appliedFilters.requestor }),
        ...(appliedFilters.poCreator && { po_creator: appliedFilters.poCreator }),
        ...(appliedFilters.destinationCountry && { destination_country: appliedFilters.destinationCountry }),
        ...(appliedFilters.plant && { plant: appliedFilters.plant }),
        ...(appliedFilters.createdDateFrom && { created_date_from: appliedFilters.createdDateFrom }),
        ...(appliedFilters.createdDateTo && { created_date_to: appliedFilters.createdDateTo }),
        ...(Array.isArray(appliedFilters.status) && appliedFilters.status.length > 0 && { status: appliedFilters.status.join(",") }),
        ...(appliedFilters.segment && { segment: appliedFilters.segment }),
        ...(Array.isArray(appliedFilters.mrsNonMrs) && appliedFilters.mrsNonMrs.length > 0 && { mrs_non_mrs: appliedFilters.mrsNonMrs.join(",") }),
        ...(appliedFilters.chargeId && { charge_id: appliedFilters.chargeId }),
        ...(appliedFilters.foNumber && { fo_number: appliedFilters.foNumber }),
        ...(appliedFilters.region && { region: appliedFilters.region }),
        ...(appliedFilters.billOfLading && { bill_of_lading: appliedFilters.billOfLading }),
        ...(appliedFilters.containerReference && { container_reference: appliedFilters.containerReference })
      });

      const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/all-charges-joined?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
          }
        }
      );

      const json = await res.json();

      setNotices(json.data || []);
      setFilteredNotices(json.data || []);
      // Coerce page/page_size to numbers: if the API echoes them back as
      // strings, they'd fail Object.is against the number-typed state this
      // effect depends on, re-triggering an identical-payload duplicate fetch.
      setPagination(prev => {
        const p = json.pagination || prev;
        return { ...p, page: Number(p.page) || prev.page, page_size: Number(p.page_size) || prev.page_size };
      });

      saveCache({
        filters: filtersRef.current,
        pagination: json.pagination,
        notices: json.data || []
      });
    } catch (err) {
      console.error(err);
    } finally {
      setTableLoading(false);
    }
  };

  fetchNotices();
}, [pagination.page, pagination.page_size, appliedFilters, isMasterSearchMode]);



  const saveCache = (data) => {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        ...data,
        timestamp: Date.now()
      })
    );
  };

  const loadCache = () => {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.timestamp > CACHE_TTL) {
        sessionStorage.removeItem(CACHE_KEY);
        return null;
      }
      return parsed;
    } catch {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
  };


  const ACTION_MAP = {
    "ERJ": "Event Charges Rejected",
    "ERT": "Event Charges Returned",
    "CRT": "Cost Charges Returned",
    "CRJ": "Cost Charges Rejected",
    "CA": "Cost Approved",
    "EA": "Event Approved",
    "PA": "PO Created",
    "IC": "Invoice Created"
  };

  const STATUS_TIMESTAMP_CATEGORY_FIELDS = [
    { code: "EA", field: "Status_TS_EA", label: "Event Approved Date" },
    { code: "ERT", field: "Status_TS_ERT", label: "Event Charges Returned Date" },
    { code: "ERJ", field: "Status_TS_ERJ", label: "Event Charges Rejected Date" },
    { code: "CA", field: "Status_TS_CA", label: "Cost Approved Date" },
    { code: "CRT", field: "Status_TS_CRT", label: "Cost Charges Returned Date" },
    { code: "CRJ", field: "Status_TS_CRJ", label: "Cost Charges Rejected Date" },
    { code: "PA", field: "Status_TS_PA", label: "PO Created Date" },
    { code: "IC", field: "Status_TS_IC", label: "Invoice Created Date" },
  ];

  const DATE_ONLY_FIELDS = [
    "CreatedDate",
    "D_D_Storage_Start_Date__c",
    "D_D_Storage_End_Date__c",
    "PO_Modified",
    "Invoice_Modified",
    "Parent_Actual_Receipt_Date_ARD__c",
    "Parent_Delivery_Actual__c",
    "Parent_ETA_Last_Vessel_arrival__c",
    "Parent_ETD__c",
    "Status_Timestamp__c",
    "Comment__c_Latest_CreatedDate",
    "Status_TS_EA",
    "Status_TS_ERT",
    "Status_TS_ERJ",
    "Status_TS_CA",
    "Status_TS_CRT",
    "Status_TS_CRJ",
    "Status_TS_PA",
    "Status_TS_IC",
  ];

  const toDateOnly = (value) => {
    if (value === null || value === undefined || value === "" || value === "-") return value;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString().split("T")[0];
    }

    const str = String(value).trim();
    if (!str) return str;

    const ddmmyyyy = str.match(/\b\d{2}-\d{2}-\d{4}\b/);
    if (ddmmyyyy) return ddmmyyyy[0];

    const yyyymmdd = str.match(/\b\d{4}-\d{2}-\d{2}\b/);
    if (yyyymmdd) return yyyymmdd[0];

    const parsed = new Date(str);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().split("T")[0];
    }

    return str;
  };

  const parseEvent = (timestampStr, codes) => {
    if (!timestampStr) return null;

    const entries = timestampStr
      .split("|")
      .map(e => e.trim())
      .filter(Boolean);

    const matched = entries.filter(e =>
      codes.some(code => e.toUpperCase().startsWith(code.toUpperCase()))
    );

    if (matched.length === 0) return null;

    const latest = matched[matched.length - 1];
    const [code, ...rest] = latest.split(" ");
    return { code, datetime: rest.join(" ").trim() };
  };

  const getStatusTimestampCategoryValues = (timestampStr) =>
    STATUS_TIMESTAMP_CATEGORY_FIELDS.reduce((acc, { code, field }) => {
      const evt = parseEvent(timestampStr, [code]);
      acc[field] = evt ? toDateOnly(evt.datetime) : "-";
      return acc;
    }, {});

  const tableDataWithTimestamps = filteredNotices.map((row) => {
    const ts = row.Status_Timestamp__c || "";

    const statusEvt = parseEvent(ts, ["ERJ", "ERT", "CA", "CRT", "CRJ", "EA", "PA", "IC"]);
    const poEvt = parseEvent(ts, ["PA"]);
    const invEvt = parseEvent(ts, ["IC"]);

    const mappedRow = {
      ...row,
      Owner_Name: row.Owner?.Name || "-",
      Status_Modified: statusEvt ? `${ACTION_MAP[statusEvt.code]} - ${toDateOnly(statusEvt.datetime)}` : "-",
      PO_Modified: poEvt ? poEvt.datetime : "-",
      Invoice_Modified: invEvt ? invEvt.datetime : "-",
      ...getStatusTimestampCategoryValues(ts),
    };

    DATE_ONLY_FIELDS.forEach((field) => {
      if (mappedRow[field] !== undefined) {
        mappedRow[field] = toDateOnly(mappedRow[field]);
      }
    });

    return mappedRow;
  });


  const handleLogout = () => {
    sessionStorage.removeItem(CACHE_KEY);
    AL.logoutRedirect({
      post_logout_uri: window.location.origin + "/"
    });
  };

  const handleRequestChange = () => {
    navigate('/requestCharge');
  };
  useEffect(() => {
    filtersRef.current = appliedFilters;
  }, [appliedFilters]);

  // Keep currentFiltersRef in sync with filters state
  useEffect(() => {
    currentFiltersRef.current = filters;
  }, [filters]);

  // Apply all filters at once - use ref to get latest values
  const applyFilters = () => {
    setAppliedFilters({ ...currentFiltersRef.current });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle Enter key to apply filters
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      applyFilters();
    }
  };

  // Handle input change for text fields
  const handleInputChange = (field) => (e) => {
    let value = null; // Use null as sentinel to detect if we found a value
    
    // Method 1: Try composedPath FIRST - gets the actual input from shadow DOM (most reliable for web components)
    if (e.composedPath && typeof e.composedPath === 'function') {
      const path = e.composedPath();
      const inputEl = path.find(el => el && el.tagName === 'INPUT');
      if (inputEl) {
        value = inputEl.value;
      }
    }
    // Method 2: e.target.value (standard input) - only if we didn't find value yet
    if (value === null && e.target && 'value' in e.target) {
      value = e.target.value;
    }
    // Method 3: e.detail.value (MDS pattern)
    if (value === null && e.detail && typeof e.detail === 'object' && 'value' in e.detail) {
      value = e.detail.value;
    }
    // Method 4: e.detail is the value itself (string)
    if (value === null && typeof e.detail === 'string') {
      value = e.detail;
    }
    
    // Default to empty string if no value found
    if (value === null) {
      value = "";
    }
    
    // Update ref immediately (synchronous) so applyFilters can access latest value
    currentFiltersRef.current = {
      ...currentFiltersRef.current,
      [field]: value
    };
    
    // Also update state for UI re-render
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle multi-select change for McMultiSelect fields
  const handleMultiSelectChange = (field) => (e) => {
    const detail = e.detail;
    const toStr = (item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') {
        return item.value ?? item.label ?? item.text ?? item.name ?? null;
      }
      return null;
    };
    let newValues = [];
    if (Array.isArray(detail)) {
      newValues = detail.map(toStr).filter(Boolean);
    } else if (detail && typeof detail === 'object') {
      const inner = detail.value ?? detail.label ?? detail.text ?? null;
      if (Array.isArray(inner)) {
        newValues = inner.map(toStr).filter(Boolean);
      } else if (typeof inner === 'string' && inner !== '') {
        const current = Array.isArray(currentFiltersRef.current[field]) ? currentFiltersRef.current[field] : [];
        newValues = current.includes(inner) ? current.filter(v => v !== inner) : [...current, inner];
      }
    } else if (typeof detail === 'string' && detail !== '') {
      const current = Array.isArray(currentFiltersRef.current[field]) ? currentFiltersRef.current[field] : [];
      newValues = current.includes(detail) ? current.filter(v => v !== detail) : [...current, detail];
    }
    currentFiltersRef.current = { ...currentFiltersRef.current, [field]: newValues };
    setFilters(prev => ({ ...prev, [field]: newValues }));
  };

  // Handle select change for dropdown fields
  const handleSelectChange = (field) => (e) => {
    const selectedValue = e.detail?.value || "";
    // Update ref immediately
    currentFiltersRef.current = {
      ...currentFiltersRef.current,
      [field]: selectedValue
    };
    setFilters((prev) => ({
      ...prev,
      [field]: selectedValue
    }));
  };

  // Handle clear button for text fields
  const handleClearField = (field) => () => {
    // Update ref immediately
    currentFiltersRef.current = {
      ...currentFiltersRef.current,
      [field]: ""
    };
    setFilters((prev) => ({ ...prev, [field]: "" }));
  };

  // Add a filter to the visible list
  const addFilter = (filterKey) => {
    if (!visibleFilters.includes(filterKey)) {
      setVisibleFilters([...visibleFilters, filterKey]);
    }
    // Don't close the selector - allow multiple selections
  };

  // Remove a filter from the visible list and clear its value
  const removeFilter = (filterKey) => {
    setVisibleFilters(visibleFilters.filter(f => f !== filterKey));
    // Clear the filter value when removing
    if (filterKey === "dateRange") {
      currentFiltersRef.current = { ...currentFiltersRef.current, createdDateFrom: null, createdDateTo: null };
      setFilters(prev => ({ ...prev, createdDateFrom: null, createdDateTo: null }));
      setAppliedFilters(prev => ({ ...prev, createdDateFrom: null, createdDateTo: null }));
    } else {
      const defaultValue = (filterKey === "status" || filterKey === "mrsNonMrs") ? [] : "";
      currentFiltersRef.current = { ...currentFiltersRef.current, [filterKey]: defaultValue };
      setFilters(prev => ({ ...prev, [filterKey]: defaultValue }));
      setAppliedFilters(prev => ({ ...prev, [filterKey]: defaultValue }));
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Get available filters (not yet selected)
  const availableFilters = FILTER_CONFIG.filter(f => !visibleFilters.includes(f.key));

  // Reset all filters and the visible filter selection
  const handleClearAllFilters = () => {
    const clearedFilters = {
      approver: "",
      requestor: "",
      poCreator: "",
      segment: "",
      mrsNonMrs: [],
      destinationCountry: "",
      plant: "",
      createdDateFrom: null,
      createdDateTo: null,
      chargeId: "",
      foNumber: "",
      region: "",
      billOfLading: "",
      containerReference: "",
      status: []
    };
    currentFiltersRef.current = clearedFilters;
    setVisibleFilters([]);
    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDateFromChange = (e) => {
    const fromDate = e?.detail ? e.detail : null;
    // Update ref immediately
    currentFiltersRef.current = {
      ...currentFiltersRef.current,
      createdDateFrom: fromDate
    };
    setFilters((prev) => ({
      ...prev,
      createdDateFrom: fromDate
    }));
  };

  const handleDateToChange = (e) => {
    const toDate = e?.detail ? e.detail : null;
    // Update ref immediately
    currentFiltersRef.current = {
      ...currentFiltersRef.current,
      createdDateTo: toDate
    };
    setFilters((prev) => ({
      ...prev,
      createdDateTo: toDate
    }));
  };

  const handlePageChange = (newPage) => {
    if (isMasterSearchMode) {
      if (newPage >= 1 && newPage <= masterPagination.total_pages && newPage !== masterPagination.page) {
        setMasterPagination(prev => ({ ...prev, page: newPage }));
      }
    } else {
      if (newPage >= 1 && newPage <= pagination.total_pages && newPage !== pagination.page) {
        setPagination(prev => ({ ...prev, page: newPage }));
      }
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    if (isMasterSearchMode) {
      setMasterPagination(prev => ({ ...prev, page: 1, page_size: newPageSize }));
    } else {
      if (newPageSize !== pagination.page_size) {
        setPagination(prev => ({
          ...prev,
          page: 1,
          page_size: newPageSize
        }));
      }
    }
  };

  const columns = [
    { id: "Parent_Name", label: "Parent ID", noWrap: true },
    { id: "Name", label: "Child ID", noWrap: true },
    { id: "Parent_FO_Number__c", label: "FO", noWrap: true },
    { id: "CreatedDate", label: "Request raised on", noWrap: true },
    { id: "Owner_Name", label: "Owner", noWrap: true },
    { id: "Parent_CBL_Reference__c", label: "CBL Reference", width: "130px" },
    { id: "Parent_Container_Number__c", label: "Container Number", width: "150px" },
    { id: "Comments__c", label: "Text", width: "150px" },
    { id: "MRS_NON_MRS__c", label: "MRS/Non-MRS", width: "110px" },
    { id: "Lane_Id__c", label: "Lane Id", noWrap: true },
    { id: "Currency__c", label: "Currency", noWrap: true },
    { id: "Origin_Destination__c", label: "Origin Charge Destination Charge", width: "130px" },
    { id: "Material_Name__c", label: "Material Name", width: "130px" },
    { id: "Maersk_Code__c", label: "Maersk Code", width: "130px" },
    { id: "Material_Name_in_MRS__c", label: "Material Name In MRS", width: "130px" },
    { id: "UOM__c", label: "UOM", width: "130px" },
    { id: "D_D_Storage_Start_Date__c", label: "D&D Storage Start", width: "130px" },
    { id: "D_D_Storage_End_Date__c", label: "D&D Storage End", width: "130px" },
    { id: "Total_Days__c", label: "Total Days", width: "130px" },
    { id: "Total_hours_waiting_time_for_charges__c", label: "Total Hours Waiting", width: "130px" },
    { id: "Quantity__c", label: "Qty", width: "130px" },
    { id: "Unit_Price__c", label: "Unit Price", width: "130px" },
    { id: "Amount__c", label: "Amount", width: "130px" },
    { id: "Conversion_Rate_USD__c", label: "Conversion Rate (USD)", width: "130px" },
    { id: "Amount_in_USD__c", label: "Amount in USD", width: "130px" },
    { id: "Parent_Plant__c", label: "Plant", width: "130px" },
    { id: "Status__c", label: "Approval Status", width: "130px" },
    { id: "Status_TS_EA", label: "Event Approved Date", width: "130px" },
    { id: "Status_TS_ERT", label: "Event Charges Returned Date", width: "130px" },
    { id: "Status_TS_ERJ", label: "Event Charges Rejected Date", width: "130px" },
    { id: "Status_TS_CA", label: "Cost Approved Date", width: "130px" },
    { id: "Status_TS_CRT", label: "Cost Charges Returned Date", width: "130px" },
    { id: "Status_TS_CRJ", label: "Cost Charges Rejected Date", width: "130px" },
    { id: "Status_TS_PA", label: "PO Created Date", width: "130px" },
    { id: "PO_Number__c", label: "PO Number", width: "130px" },
    { id: "Status_TS_IC", label: "Invoice Created Date", width: "130px" },
    { id: "Invoice_Number__c", label: "Invoice Number", width: "130px" },
    {id:"Reason_For_Invalid_Charge__c", label: "Reason For Invalid Charge", width: "130px"},
    {id:"Parent_Segment__c", label: "Segment", width: "130px"}
  ];


  const exportMapping = [
    { field: "Parent_Name", header: "Parent ID" },
    { field: "Name", header: "Child ID" },
    { field: "CreatedDate", header: "Request raised on" },
    { field: "Parent_FO_Number__c", header: "FO Number" },
    { field: "Owner_Name", header: "Owner" },
    { field: "Parent_CBL_Reference__c", header: "CBL Reference" },
    { field: "Parent_Container_Number__c", header: "Container Number" },
    { field: "Comments__c", header: "Text" },
    { field: "MRS_NON_MRS__c", header: "MRS/NON MRS" },
    { field: "Lane_Id__c", header: "Lane Id" },
    { field: "Currency__c", header: "Currency" },
    { field: "Origin_Destination__c", header: "Origin/Destination" },
    { field: "Material_Name__c", header: "Material Name" },
    { field: "Maersk_Code__c", header: "Maersk Code" },
    { field: "Material_Name_in_MRS__c", header: "Material Name in MRS" },
    { field: "UOM__c", header: "UOM" },
    { field: "D_D_Storage_Start_Date__c", header: "D&D / Storage - Start Date" },
    { field: "D_D_Storage_End_Date__c", header: "D&D / Storage - End Date" },
    { field: "Total_Days__c", header: "Total Days" },
    { field: "Total_hours_waiting_time_for_charges__c", header: "Total hours waiting time for charges" },
    { field: "Quantity__c", header: "Quantity" },
    { field: "Unit_Price__c", header: "Unit Price" },
    { field: "Amount__c", header: "Amount" },
    { field: "Conversion_Rate_USD__c", header: "Conversion Rate (USD)" },
    { field: "Amount_in_USD__c", header: "Amount in USD" },
    { field: "Parent_Plant__c", header: "Plant" },
    { field: "Status__c", header: "Approval status" },
    { field: "Status_Modified", header: "Status modified" },
    { field: "Status_TS_EA", header: "Event Approved Date" },
    { field: "Status_TS_ERT", header: "Event Charges Returned Date" },
    { field: "Status_TS_ERJ", header: "Event Charges Rejected Date" },
    { field: "Status_TS_CA", header: "Cost Approved Date" },
    { field: "Status_TS_CRT", header: "Cost Charges Returned Date" },
    { field: "Status_TS_CRJ", header: "Cost Charges Rejected Date" },
    { field: "Status_TS_PA", header: "PO Created Date" },
    { field: "PO_Number__c", header: "PO number" },
    { field: "Status_TS_IC", header: "Invoice Created Date" },
    { field: "Invoice_Number__c", header: "Invoice number" },
    { field: "Parent_Actual_Receipt_Date_ARD__c", header: "Actual Receipt Date (ARD)" },
    { field: "Event_Approver__c", header: "Event Approver" },
    { field: "Parent_Container_Size__c", header: "Container Size" },
    { field: "Parent_Container_Type__c", header: "Container Type" },
    { field: "Parent_Delivery_Actual__c", header: "Delivery Actual" },
    { field: "Parent_Destination_Region__c", header: "Destination Region" },
    { field: "Parent_ETA_Last_Vessel_arrival__c", header: "ETA Last Vessel arrival" },
    { field: "Parent_ETD__c", header: "ETD" },
    { field: "Parent_Freight_Payer_Country_Code__c", header: "Freight Payer Country (Code)" },
    { field: "Parent_Freight_Payer_Country_Name__c", header: "Freight Payer Country (Name)" },
    { field: "Parent_Incoterms__c", header: "Incoterms" },
    { field: "Invoice_Creator__c", header: "Invoice Creator" },
    { field: "Parent_Invoice_team__c", header: "Invoice team" },
    { field: "Parent_Original_Port_Of_Loading_City_Code__c", header: "Original Port Of Loading City (Code)" },
    { field: "Parent_Original_Port_Of_Loading_City_Name__c", header: "Original Port Of Loading City (Name)" },
    { field: "Parent_Place_Of_Delivery_City_Code__c", header: "Place Of Delivery City (Code)" },
    { field: "Parent_Place_Of_Delivery_City_Name__c", header: "Place Of Delivery City (Name)" },
    { field: "Parent_Place_Of_Receipt_City_Code__c", header: "Place Of Receipt City (Code)" },
    { field: "Parent_Place_Of_Receipt_City_Name__c", header: "Place Of Receipt City (Name)" },
    { field: "PO_Creator__c", header: "PO Creator" },
    { field: "Cost_Approver__c", header: "Cost Approver" },
    { field: "Parent_Port_Of_Discharge_City_Code__c", header: "Port Of Discharge City (Code)" },
    { field: "Parent_Port_Of_Discharge_City_Name__c", header: "Port Of Discharge City (Name)" },
    { field: "Parent_Prod__c", header: "Prod" },
    { field: "Parent_Send_Email__c", header: "Send Email" },
    { field: "Parent_Ship_From_City__c", header: "Ship From City" },
    { field: "Parent_Ship_From_Country_Code__c", header: "Ship From Country (Code)" },
    { field: "Parent_Ship_From_Country_Name__c", header: "Ship From Country (Name)" },
    { field: "Parent_Ship_From_Name__c", header: "Ship From Name" },
    { field: "Parent_Ship_From_Street__c", header: "Ship From Street" },
    { field: "Parent_Ship_To_City__c", header: "Ship To City" },
    { field: "Parent_Ship_To_Name__c", header: "Ship To Name" },
    { field: "Parent_Ship_To_Street__c", header: "Ship To Street" },
    { field: "Parent_Segment__c", header: "Segment" },
    { field: "Parent_Status__c", header: "Status (Charge Notice Record)" },
    { field: "Parent_Id", header: "Charge Notice" },
    { field: "Comments__c", header: "Comments" },
    { field: "Reason_For_Rejection_Return__c", header: "Reason For Rejection/Return" },
    { field: "Reason_For_Invalid_Charge__c", header: "Reason For Invalid Charge" },
    { field: "Status__c", header: "Status (Charge Record)" },
    { field: "Status_Timestamp__c", header: "Status Timestamp" },
    { field: "Link", header: "Link" },
    { field: "Credit_Note__c", header: "Credit Note Number" }
  ];

  const exportCommentsMapping = [
    { field: "Comment__c_Count", header: "Comments Count" },
    { field: "Comment__c_Latest_Description", header: "Latest Comment" },
    { field: "Comment__c_Latest_From", header: "Latest Comment From" },
    { field: "Comment__c_Latest_CreatedDate", header: "Latest Comment Date" },
    { field: "Comment__c_Combined", header: "All Comments (Combined)" },
  ];
  // Check if any filters are applied
  const hasActiveFilters = useMemo(() => {
    return Object.values(appliedFilters).some(value => {
      if (Array.isArray(value)) return value.length > 0;
      return value && value !== "";
    });
  }, [appliedFilters]);

  const handleExport = async () => {
    const isMasterSearch = isMasterSearchModeRef.current;
    const currentQuery = masterSearchQueryRef.current;

    // ── Comments + date range validation (filter mode only) ──────────────
    if (includeCommentsRef.current && !isMasterSearch) {
      const cf = filtersRef.current;
      if (cf.createdDateFrom && cf.createdDateTo) {
        const from = new Date(cf.createdDateFrom);
        const to = new Date(cf.createdDateTo);
        const months =
          (to.getFullYear() - from.getFullYear()) * 12 +
          (to.getMonth() - from.getMonth()) +
          (to.getDate() > from.getDate() ? 0 : -1);
        if (months >= 3) {
          alert(
            "When \"Comments\" is enabled, the date range must be less than 3 months.\n\nPlease narrow your date range or disable the Comments toggle."
          );
          return;
        }
      } else {
        alert(
          "When \"Comments\" is enabled, please set a date range of less than 3 months to limit the data size."
        );
        return;
      }
    }

    // ── Master Search download: stream Excel from backend ────────────────
    if (isMasterSearch) {
      if (!currentQuery) return;
      setLoading(true);
      try {
        const queryParams = new URLSearchParams({
          query: currentQuery,
          pageNumber: 1,
          pageSize: 200,
          download: "true",
          ...(includeCommentsRef.current && { include_comments: "true" }),
        });
        const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/master-search?${queryParams}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY,
            },
          }
        );
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const today = new Date().toISOString().split("T")[0];
        a.download = `${today}_master_search.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Master search download error:", err);
        alert("Failed to download master search results. Please try again.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // ── Regular filter-based export: stream Excel from backend ──────────
    const currentFilters = { ...filtersRef.current };

    const hasActive = Object.values(currentFilters).some(value => Array.isArray(value) ? value.length > 0 : (value && value !== ""));
    if (!hasActive) {
      alert('Please apply at least one filter before downloading data.');
      return;
    }

    try {
      setLoading(true);

      const queryParams = new URLSearchParams({
        download: "true",
        ...(currentFilters.approver && { approver: currentFilters.approver }),
        ...(currentFilters.requestor && { requestor: currentFilters.requestor }),
        ...(currentFilters.poCreator && { po_creator: currentFilters.poCreator }),
        ...(currentFilters.destinationCountry && { destination_country: currentFilters.destinationCountry }),
        ...(currentFilters.plant && { plant: currentFilters.plant }),
        ...(currentFilters.createdDateFrom && { created_date_from: currentFilters.createdDateFrom }),
        ...(currentFilters.createdDateTo && { created_date_to: currentFilters.createdDateTo }),
        ...(Array.isArray(currentFilters.status) && currentFilters.status.length > 0 && { status: currentFilters.status.join(",") }),
        ...(currentFilters.segment && { segment: currentFilters.segment }),
        ...(Array.isArray(currentFilters.mrsNonMrs) && currentFilters.mrsNonMrs.length > 0 && { mrs_non_mrs: currentFilters.mrsNonMrs.join(",") }),
        ...(currentFilters.chargeId && { charge_id: currentFilters.chargeId }),
        ...(currentFilters.foNumber && { fo_number: currentFilters.foNumber }),
        ...(currentFilters.region && { region: currentFilters.region }),
        ...(currentFilters.billOfLading && { bill_of_lading: currentFilters.billOfLading }),
        ...(currentFilters.containerReference && { container_reference: currentFilters.containerReference }),
        ...(includeCommentsRef.current && { include_comments: "true" }),
      });

      const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/all-charges-joined?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY,
          },
        }
      );

      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().split("T")[0];
      a.download = `${today}_FO_Charges.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error("❌ Error during export:", error);
      alert("Failed to export data. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  const handlePaginationChange = (e) => {
    const newPage = e.detail?.page || e.detail;
    if (newPage && newPage !== pagination.page) {
      handlePageChange(newPage);
    }
  };

  const safeSelectValue = (value, options) =>
  options.includes(value) ? value : "";

  return (
    <div className="home-wrapper">
      <McTopBar
        product="Maersk | Unplanned Charges"
        productshort="Maersk"
        className="app-topbar"
      >
        <div slot="actions" className="topbar-actions">
          {/* User avatar dropdown */}
          <div ref={userMenuRef} style={{ position: "relative" }}>
            <button
              onClick={() => setUserMenuOpen((o) => !o)}
              title={userInfo || "User"}
              style={{
                width: "34px",
                height: "34px",
                borderRadius: "50%",
                backgroundColor: "#42b0d5",
                color: "#ffffff",
                border: "2px solid rgba(255,255,255,0.5)",
                fontSize: "0.72rem",
                fontWeight: "400",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                letterSpacing: "0.06em",
                flexShrink: 0,
                fontFamily: "Maersk Headline, MaerskText, sans-serif",
                transition: "background-color 0.15s ease",
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#2e9dbf"}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#42b0d5"}
            >
              {getUserInitials(userInfo)}
            </button>

            {userMenuOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 10px)",
                  right: 0,
                  minWidth: "220px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e0e0e0",
                  borderRadius: "16px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  zIndex: 9999,
                }}
              >
                {/* Caret arrow pointing up to avatar */}
                <div style={{
                  position: "absolute",
                  top: "-7px",
                  right: "10px",
                  width: 0,
                  height: 0,
                  borderLeft: "7px solid transparent",
                  borderRight: "7px solid transparent",
                  borderBottom: "7px solid #e0e0e0",
                }} />
                <div style={{
                  position: "absolute",
                  top: "-6px",
                  right: "10px",
                  width: 0,
                  height: 0,
                  borderLeft: "7px solid transparent",
                  borderRight: "7px solid transparent",
                  borderBottom: "7px solid #ffffff",
                }} />
                {/* User info header */}
                <div
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid #e0e0e0",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "16px 16px 0 0",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      backgroundColor: "#42b0d5",
                      color: "#fff",
                      fontSize: "13px",
                      fontWeight: "400",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      letterSpacing: "0.5px",
                    }}
                  >
                    {getUserInitials(userInfo)}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p style={{
                      margin: 0,
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#1a1a1a",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "160px",
                    }}>
                      {userInfo || "User"}
                    </p>
                  </div>
                </div>

                {/* Menu items */}
                <ul style={{ listStyle: "none", margin: 0, padding: "4px 0" }}>
                  {hasAccess && (
                    <li>
                      <button
                        onClick={() => { handleRequestChange(); setUserMenuOpen(false); }}
                        style={{
                          width: "100%",
                          padding: "10px 16px 10px 46px",
                          background: "none",
                          border: "none",
                          textAlign: "left",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#1a1a1a",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          fontFamily: "inherit",
                          letterSpacing: "0.01em",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e8f4fb"}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                      >
                        Request Charge
                      </button>
                    </li>
                  )}
                  {hasAccessbulk && (
                    <>
                      <li style={{ height: "1px", backgroundColor: "#e0e0e0", margin: "4px 0" }} />
                      <li>
                        <button
                          onClick={() => { navigate('/bulk-invoice-upload'); setUserMenuOpen(false); }}
                          style={{
                            width: "100%",
                            padding: "10px 16px 10px 46px",
                            background: "none",
                            border: "none",
                            textAlign: "left",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#1a1a1a",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            fontFamily: "inherit",
                            letterSpacing: "0.01em",
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e8f4fb"}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                        >
                          Bulk Upload
                        </button>
                      </li>
                    </>
                  )}
                  {hasReassignAccess && (
                    <>
                      <li style={{ height: "1px", backgroundColor: "#e0e0e0", margin: "4px 0" }} />
                      <li>
                        <button
                          onClick={() => { navigate('/bulk-reassign'); setUserMenuOpen(false); }}
                          style={{
                            width: "100%",
                            padding: "10px 16px 10px 46px",
                            background: "none",
                            border: "none",
                            textAlign: "left",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#1a1a1a",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            fontFamily: "inherit",
                            letterSpacing: "0.01em",
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e8f4fb"}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                        >
                          Bulk Reassign
                        </button>
                      </li>
                    </>
                  )}
                  {(hasAccess || hasAccessbulk || hasReassignAccess || hasAnalyticsAccess) && hasApproverMatrixAccess && (
                    <li style={{ height: "1px", backgroundColor: "#e0e0e0", margin: "4px 0" }} />
                  )}
                  {hasApproverMatrixAccess && (
                    <li>
                      <button
                        onClick={() => { navigate("/approver-matrix"); setUserMenuOpen(false); }}
                        style={{
                          width: "100%",
                          padding: "10px 16px",
                          background: "none",
                          border: "none",
                          textAlign: "left",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#1a1a1a",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          fontFamily: "inherit",
                          letterSpacing: "0.01em",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e8f4fb"}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                      >
                        <McIcon icon="user-multiple" size="20"></McIcon>
                        Approver Matrix
                      </button>
                    </li>
                  )}

                  {(hasAccess || hasAccessbulk || hasReassignAccess || hasAnalyticsAccess || hasApproverMatrixAccess) && (
                    <li style={{ height: "1px", backgroundColor: "#e0e0e0", margin: "4px 0" }} />
                  )}

                  <li>
                    <button
                      onClick={() => { handleLogout(); setUserMenuOpen(false); }}
                      style={{
                        width: "100%",
                        padding: "10px 16px",
                        background: "none",
                        border: "none",
                        textAlign: "left",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#dc2626",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        fontFamily: "inherit",
                        letterSpacing: "0.01em",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#fef2f2"}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                    >
                      <McIcon icon="log-out" size="20"></McIcon>
                      Sign Out
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </McTopBar>

      <div className="home-container">
        <div className="content-wrapper mds">
          {/* ── Page Header + Master Search + Download ─────────────────── */}
          <div className="page-header-bar">
            {/* Left: title */}
            <h3 className="main-header">All FO Charges</h3>

            {/* Centre: master search */}
            <div className={`master-search-wrapper${isMasterSearchMode ? " active" : ""}`}>
              <span className="master-search-icon">
                {tableLoading && isMasterSearchMode
                  ? <span className="search-spinner" />
                  : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                }
              </span>
              <input
                className="master-search-input"
                type="text"
                placeholder="Search across ID, FO, container, approver, PO, invoice…"
                value={masterSearchInput}
                onChange={(e) => setMasterSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && masterSearchInput.trim()) {
                    const q = masterSearchInput.trim();
                    masterSearchQueryRef.current = q;
                    isMasterSearchModeRef.current = true;
                    setMasterSearchQuery(q);
                    setMasterPagination(prev => ({ ...prev, page: 1 }));
                    setIsMasterSearchMode(true);
                  }
                  if (e.key === "Escape") {
                    isMasterSearchModeRef.current = false;
                    masterSearchQueryRef.current = "";
                    setMasterSearchInput("");
                    setMasterSearchQuery("");
                    setIsMasterSearchMode(false);
                    setNotices([]);
                    setFilteredNotices([]);
                  }
                }}
              />
              {masterSearchInput && (
                <button
                  className="master-search-clear"
                  onClick={() => {
                    isMasterSearchModeRef.current = false;
                    masterSearchQueryRef.current = "";
                    setMasterSearchInput("");
                    setMasterSearchQuery("");
                    setIsMasterSearchMode(false);
                    setNotices([]);
                    setFilteredNotices([]);
                  }}
                  title="Clear search"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              )}
              <button
                className="master-search-btn"
                disabled={!masterSearchInput.trim() || tableLoading}
                onClick={() => {
                  const q = masterSearchInput.trim();
                  if (!q) return;
                  masterSearchQueryRef.current = q;
                  isMasterSearchModeRef.current = true;
                  setMasterSearchQuery(q);
                  setMasterPagination(prev => ({ ...prev, page: 1 }));
                  setIsMasterSearchMode(true);
                }}
              >
                Search
              </button>
            </div>

            {/* Right: status pill + back + download */}
            <div className="header-right-actions">
              {isMasterSearchMode && (
                <>
                  <span className="master-search-result-pill">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:"5px"}}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    {tableLoading ? "Searching…" : `${masterPagination.total_records} results for "${masterSearchQuery}"`}
                  </span>
                  <button
                    className="back-to-filters-btn"
                    onClick={() => {
                      isMasterSearchModeRef.current = false;
                      masterSearchQueryRef.current = "";
                      setMasterSearchInput("");
                      setMasterSearchQuery("");
                      setIsMasterSearchMode(false);
                      setNotices([]);
                      setFilteredNotices([]);
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                  >
                    ← Filters
                  </button>
                </>
              )}
              <McButton
                className="export-button"
                onClick={handleExport}
                label={loading ? "Downloading…" : (isMasterSearchMode || hasActiveFilters) ? "Download" : "Apply Filters to Download"}
                appearance={(isMasterSearchMode || hasActiveFilters) ? "secondary" : "neutral"}
                fit="small"
                disabled={(!isMasterSearchMode && !hasActiveFilters) || loading}
                title={isMasterSearchMode ? "Download master search results as Excel" : hasActiveFilters ? "Download data with current filters" : "Apply at least one filter to download"}
              />
              {/* Include Comments toggle */}
              <div
                className={`comments-toggle-wrap${includeComments ? " on" : ""}`}
                onClick={() => {
                  const next = !includeCommentsRef.current;
                  includeCommentsRef.current = next;
                  setIncludeComments(next);
                }}
                title={includeComments ? "Comments included in download — click to exclude" : "Comments excluded from download — click to include"}
              >
                <span className="comments-toggle-label">Comments</span>
                <div className="toggle-track">
                  <div className="toggle-thumb" />
                </div>
              </div>
            </div>
          </div>

          <div className="filters-section" style={{ display: isMasterSearchMode ? "none" : undefined }}>
            <FilterSelector
              filterConfig={FILTER_CONFIG}
              visibleKeys={visibleFilters}
              onAdd={addFilter}
              onRemove={removeFilter}
              onClearAll={handleClearAllFilters}
              emptyStateText="Select filters to search and filter the data."
            />

            {/* Dynamic filter inputs */}
            {visibleFilters.length > 0 && (
              <>
                <div className="filters-row" onKeyDown={handleKeyDown}>
                  {visibleFilters.map(filterKey => {
                    const filterConfig = FILTER_CONFIG.find(f => f.key === filterKey);
                    if (!filterConfig) return null;

                    if (filterConfig.type === "text") {
                      return (
                        <McInput
                          key={filterKey}
                          label={filterConfig.label}
                          placeholder={filterConfig.placeholder}
                          clearbutton
                          value={filters[filterKey]}
                          input={handleInputChange(filterKey)}
                          clearbuttonclick={handleClearField(filterKey)}
                        />
                      );
                    }

                    if (filterConfig.type === "multiselect" && filterKey === "status") {
                      return (
                        <McMultiSelect
                          key={filterKey}
                          label={filterConfig.label}
                          placeholder={filterConfig.placeholder}
                          optionselected={handleMultiSelectChange("status")}
                        >
                          {(filterOptions.status || []).map((t) => (
                            <McOption key={t} value={t}>{t}</McOption>
                          ))}
                        </McMultiSelect>
                      );
                    }

                    if (filterConfig.type === "select" && filterKey === "segment") {
                      return (
                        <McSelect
                          key={filterKey}
                          label={filterConfig.label}
                          placeholder={filterConfig.placeholder}
                          value={safeSelectValue(filters.segment, filterOptions.segment)}
                          optionselected={handleSelectChange("segment")}
                        >
                          {filterOptions.segment.map((t) => (
                            <McOption key={t} value={t}>{t}</McOption>
                          ))}
                        </McSelect>
                      );
                    }

                    if (filterConfig.type === "multiselect" && filterKey === "mrsNonMrs") {
                      return (
                        <McMultiSelect
                          key={filterKey}
                          label={filterConfig.label}
                          placeholder={filterConfig.placeholder}
                          optionselected={handleMultiSelectChange("mrsNonMrs")}
                        >
                          {(filterOptions.mrs_non_mrs || []).map((t) => (
                            <McOption key={t} value={t}>{t}</McOption>
                          ))}
                        </McMultiSelect>
                      );
                    }

                    if (filterConfig.type === "dateRange") {
                      return (
                        <div key={filterKey} className="date-range-horizontal">
                          <McInputDate
                            yearcap={20}
                            label="From Date"
                            placeholder="YYYY-MM-DD"
                            name="from-date"
                            clearbutton
                            value={filters.createdDateFrom || ""}
                            inputdateselected={handleDateFromChange}
                            clearbuttonclick={() => {
                              currentFiltersRef.current = { ...currentFiltersRef.current, createdDateFrom: null };
                              setFilters(prev => ({
                                ...prev,
                                createdDateFrom: null
                              }));
                            }}
                          />
                          <McInputDate
                            yearcap={20}
                            label="To Date"
                            placeholder="YYYY-MM-DD"
                            name="to-date"
                            clearbutton
                            value={filters.createdDateTo || ""}
                            inputdateselected={handleDateToChange}
                            clearbuttonclick={() => {
                              currentFiltersRef.current = { ...currentFiltersRef.current, createdDateTo: null };
                              setFilters(prev => ({
                                ...prev,
                                createdDateTo: null
                              }));
                            }}
                          />
                        </div>
                      );
                    }

                    return null;
                  })}
                </div>
                <div style={{ marginTop: '1rem' }}>
                  <McButton
                    label="Apply Filters"
                    appearance="primary"
                    fit="small"
                    onClick={applyFilters}
                    disabled={tableLoading || !Object.values(filters).some(value => Array.isArray(value) ? value.length > 0 : (value && value !== ""))}
                  />
                </div>
              </>
            )}


          </div>

          <div className={`table-container ${tableLoading ? 'table-loading' : ''}`}>
            {tableLoading && (
              <div className="table-loader-overlay">
                <div className="table-loader">
                  <div className="loader"></div>
                  <span className="loading-text">Loading data...</span>
                </div>
              </div>
            )}
            <div style={{
              border: tableDataWithTimestamps.length > 10 ? "1px solid #ddd" : "none",
              borderRadius: "8px",
              overflow: "hidden",
              height: "80vh",
              display: "flex",
              flexDirection: "column",
              position: "relative"
            }}>

              <McTable
                data={tableDataWithTimestamps}
                datakey="Id"
                zebrastripes
                headersticky={tableDataWithTimestamps.length > 10}
                truncate
                height="auto"
                fit="small"
                columnresize
                sortdefaultcolumnid="Name"
                sortdefaultdirection="descending"
                recordsperpage={tableDataWithTimestamps.length || pagination.page_size}
                columns={columns.map((col) => ({
                  ...col,
                  cellTemplate: (e) => {
                    const val = e.rowData[col.id] || "-";
                    const displayVal = val.toString();
                    const isLengthy = displayVal.length > 20;

                    if (col.id === "Parent_Name") {
                      const parentId = e.rowData.Parent_Id;
                      if (!parentId) {
                        return e.html`<span class="table-cell-content">${displayVal}</span>`;
                      }
                      const noticeUrl = `${window.location.origin}/notice/${parentId}`;
                      return e.html`
                        <a
                          class="parent-id-link"
                          href="${noticeUrl}"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          ${displayVal}
                        </a>`;
                    }

                    // For Material Name and Maersk Code columns, show Material Description as tooltip
                    const isMaterialName = col.id === "Material_Name__c";
                    const isMaerskCode = col.id === "Maersk_Code__c";
                    const materialDescription = e.rowData.Charge_Description__c || "";
                    const materialName = e.rowData.Material_Name__c || "";

                    let tooltipText = "";
                    if (isMaterialName) {
                      // For Material Name:
                      // - If truncated + description: "Material Name: ... / Description: ..."
                      // - If truncated + no description: "Full Name"
                      // - If not truncated + description: just description
                      // - If not truncated + no description: full name
                      if (isLengthy) {
                        tooltipText = materialDescription ? `Material Name: ${displayVal}\nDescription: ${materialDescription}` : displayVal;
                      } else {
                        tooltipText = materialDescription || displayVal;
                      }
                    } else if (isMaerskCode) {
                      // For Maersk Code: show Material Name + description if available, else just Maersk Code
                      if (materialDescription && materialName) {
                        tooltipText = `Material Name: ${materialName}\nDescription: ${materialDescription}`;
                      } else {
                        tooltipText = displayVal;
                      }
                    } else {
                      tooltipText = isLengthy ? displayVal : "";
                    }

                    return e.html`
                          <span
                            title="${tooltipText}"
                            class="table-cell-content ${isLengthy ? 'lengthy-content' : ''}"
                            data-tooltip="${tooltipText}"
                          >
                            ${isLengthy ? displayVal.substring(0, 17) + '...' : displayVal}
                          </span>`;
                  },
                }))}
                style={{
                  border: "none",
                  minHeight: "100%"
                }}
              />
              
              {!tableLoading && tableDataWithTimestamps.length === 0 && (
                <div style={{
                  position: "absolute",
                  top: "100px",
                  left: "0",
                  right: "0",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "3rem",
                  color: "#666",
                  fontSize: "1rem",
                  fontStyle: "italic"
                }}>
                  No records found with the selected filters
                </div>
              )}

            </div>
          </div>

          <div className={`pagination-section ${tableLoading ? 'loading' : ''}`}>
            <div className="pagination-info">
              <div className="pagination-stats">
                <span className="results-count">
                  {tableLoading ? 'Loading...' : `${isMasterSearchMode ? masterPagination.total_records : pagination.total_records} results`}
                </span>
                <span className="page-indicator">
                  Page {isMasterSearchMode ? masterPagination.page : pagination.page} of {isMasterSearchMode ? masterPagination.total_pages : pagination.total_pages}
                </span>
              </div>

              <McSelect
                label="Show"
                value={(isMasterSearchMode ? masterPagination.page_size : pagination.page_size).toString()}
                optionselected={(e) => handlePageSizeChange(parseInt(e.detail.value))}
                disabled={tableLoading}
                className="page-size-select"
              >
                <McOption value="25">25</McOption>
                <McOption value="50">50</McOption>
                <McOption value="100">100</McOption>
                <McOption value="200">200</McOption>
              </McSelect>
            </div>

            <div className="pagination-navigation">
              <McPagination
                currentpage={isMasterSearchMode ? masterPagination.page : pagination.page}
                totalpages={isMasterSearchMode ? masterPagination.total_pages : pagination.total_pages}
                visiblepages={5}
                fit="small"
                hiddenlabel
                pagechange={handlePaginationChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
